// MainPage.js updates
import { useState, useEffect, useCallback } from 'react';
import SpotifyWebApi from 'spotify-web-api-js';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import WebPlayback from './WebPlayback';

const spotifyApi = new SpotifyWebApi();

const MainPage = ({ token }) => {
  const [playbackState, setPlaybackState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [volume, setVolume] = useState(50);

  const fetchPlaybackState = useCallback(async () => {
    if (!token) return;
    try {
      spotifyApi.setAccessToken(token);
      const response = await spotifyApi.getMyCurrentPlaybackState();
      if (response) {
        setPlaybackState(response);
        setVolume(response.device?.volume_percent || 50);
      }
    } catch (err) {
      console.error('Error fetching playback:', err);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchPlaybackState();
    const interval = setInterval(fetchPlaybackState, 1000);
    return () => clearInterval(interval);
  }, [fetchPlaybackState]);

  const handlePlayPause = async () => {
    try {
      if (playbackState?.is_playing) {
        await spotifyApi.pause();
      } else {
        await spotifyApi.play();
      }
      await fetchPlaybackState();
    } catch (err) {
      setError('Failed to toggle playback. Make sure you have a Premium account.');
    }
  };

  const handlePrevious = async () => {
    try {
      await spotifyApi.skipToPrevious();
      await fetchPlaybackState();
    } catch (err) {
      setError('Failed to skip to previous track');
    }
  };

  const handleNext = async () => {
    try {
      await spotifyApi.skipToNext();
      await fetchPlaybackState();
    } catch (err) {
      setError('Failed to skip to next track');
    }
  };

  const handleToggleShuffle = async () => {
    try {
      await spotifyApi.setShuffle(!playbackState?.shuffle_state);
      await fetchPlaybackState();
    } catch (err) {
      setError('Failed to toggle shuffle mode');
    }
  };

  const handleVolumeChange = async (e) => {
    const newVolume = parseInt(e.target.value);
    setVolume(newVolume);
    try {
      await spotifyApi.setVolume(newVolume);
    } catch (err) {
      setError('Failed to change volume');
    }
  };

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  
  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-9 p-4">
            <div className="d-flex align-items-center">
              <h2 className="me-3">Loading player...</h2>
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="container-fluid">
        <WebPlayback token={token} />

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

        <div className="row">
          <div className="col-12">
            <h2 className="mb-4">Now Playing</h2>
              {playbackState?.item ? (
                <div className="card">
                  <div className="row g-0">
                    <div className="col-md-4">
                      {playbackState.item.album?.images?.[0] && (
                        <img 
                          src={playbackState.item.album.images[0].url}
                          className="img-fluid rounded-start"
                          alt={playbackState.item.name}
                          style={{ width: '100%', height: 'auto' }}
                        />
                      )}
                    </div>
                    <div className="col-md-8">
                      <div className="card-body">
                        <h5 className="card-title">{playbackState.item.name}</h5>
                        <p className="card-text">
                          {playbackState.item.artists?.map(artist => artist.name).join(', ')}
                          <br />
                          <small className="text-muted">
                            From: {playbackState.item.album?.name}
                          </small>
                        </p>
                        
                        {/* Progress bar */}
                        <div className="progress mb-3" style={{ height: '4px' }}>
                          <div 
                            className="progress-bar bg-success" 
                            style={{ 
                              width: `${(playbackState.progress_ms / playbackState.item.duration_ms) * 100}%`
                            }}
                          ></div>
                        </div>
                        
                        {/* Time indicators */}
                        <div className="d-flex justify-content-between mb-3">
                          <small>{formatTime(playbackState.progress_ms)}</small>
                          <small>{formatTime(playbackState.item.duration_ms)}</small>
                        </div>

                        {/* Playback controls */}
                        <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
                        <button 
                            className={`btn ${playbackState.shuffle_state ? 'btn-success' : 'btn-outline-secondary'}`}
                            onClick={handleToggleShuffle}
                            title="Toggle shuffle"
                          >
                            <i className="bi bi-shuffle"></i>
                          </button>
                          <button 
                            className="btn btn-outline-secondary"
                            onClick={handlePrevious}
                          >
                            <i className="bi bi-skip-backward-fill"></i>
                          </button>
                          
                          <button 
                            className="btn btn-success rounded-circle"
                            onClick={handlePlayPause}
                            style={{ width: '50px', height: '50px' }}
                          >
                            <i className={`bi ${playbackState.is_playing ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
                          </button>
                          
                          <button 
                            className="btn btn-outline-secondary"
                            onClick={handleNext}
                          >
                            <i className="bi bi-skip-forward-fill"></i>
                          </button>
                        </div>

                        {/* Volume control */}
                        <div className="d-flex align-items-center gap-2">
                          <i className="bi bi-volume-down"></i>
                          <input 
                            type="range" 
                            className="form-range" 
                            min="0" 
                            max="100" 
                            value={volume}
                            onChange={handleVolumeChange}
                          />
                          <i className="bi bi-volume-up"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="alert alert-info">
                  No track currently playing. Head to the Playlists section to start playing music!
                </div>
              )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default MainPage;